import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaCalendar,
  FaTrophy,
  FaCertificate,
  FaBullhorn,
  FaArrowRight,
  FaMapMarkerAlt,
  FaBuilding,
  FaEye,
  FaSpinner,
  FaSearch,
  FaNewspaper,
} from 'react-icons/fa';
import { newsAPI } from '../../config/api';

const News = () => {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { value: 'All', label: '🌟 All News', icon: FaNewspaper },
    { value: 'Achievement', label: '🏆 Achievements', icon: FaTrophy },
    { value: 'Certification', label: '📜 Certifications', icon: FaCertificate },
    { value: 'Announcement', label: '📢 Announcements', icon: FaBullhorn },
    { value: 'Partnership', label: '🤝 Partnerships', icon: FaBuilding },
  ];

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔵 Fetching news from API...');
      const response = await newsAPI.getAll();
      console.log('✅ News data received:', response.data);
      
      // Filter only published news
      const newsData = Array.isArray(response.data) 
        ? response.data.filter(item => item.status === 'published')
        : [];
      
      setNews(newsData);
      setFilteredNews(newsData);
    } catch (error) {
      console.error('❌ Error fetching news:', error);
      setError('Failed to load news. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `https://worganaturals.com${imagePath}`;
  };

  const filterNews = useCallback(() => {
    let filtered = [...news];

    // Filter by category
    if (filter !== 'All') {
      filtered = filtered.filter((item) => item.category === filter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.content?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredNews(filtered);
  }, [news, filter, searchTerm]);

  useEffect(() => {
    fetchNews();
  }, []);

  useEffect(() => {
    filterNews();
  }, [filterNews]);

  const getIcon = (category) => {
    const categoryObj = categories.find((cat) => cat.value === category);
    return categoryObj?.icon || FaNewspaper;
  };

  const getCategoryColor = (category) => {
    const colors = {
      Achievement: 'from-yellow-400 to-orange-500',
      Certification: 'from-green-400 to-emerald-600',
      Announcement: 'from-blue-400 to-indigo-600',
      Partnership: 'from-purple-400 to-pink-600',
      default: 'from-gray-400 to-gray-600',
    };
    return colors[category] || colors.default;
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      Achievement: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      Certification: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      Announcement: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      Partnership: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
    return colors[category] || colors.default;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-dark-900 dark:to-dark-800">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <FaSpinner className="animate-spin text-6xl text-green-600 dark:text-green-400 mx-auto mb-4" />
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Loading latest news...
          </p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-dark-900 dark:to-dark-800 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center bg-white dark:bg-dark-700 p-8 rounded-2xl shadow-xl max-w-md"
        >
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            Error Loading News
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={fetchNews}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-semibold"
          >
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-green-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900">
      {/* Header */}
      <div className="pt-32 pb-12 px-6 md:px-12 lg:px-20">
        <div className="container mx-auto max-w-7xl">
          {/* Title Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-3 bg-green-100 dark:bg-green-900/30 px-6 py-3 rounded-full mb-6">
              <FaNewspaper className="text-green-600 dark:text-green-400 text-2xl" />
              <span className="text-green-800 dark:text-green-300 font-semibold text-lg">
                Latest Updates
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
              News & Achievements
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Stay updated with our latest milestones, certifications, and company announcements
            </p>
          </motion.div>

          {/* Search and Filter Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <div className="bg-white dark:bg-dark-700 rounded-2xl shadow-xl p-6">
              {/* Search Bar */}
              <div className="relative mb-6">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search news..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-dark-600 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-transparent text-gray-900 dark:text-white transition"
                />
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-3 justify-center">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <motion.button
                      key={category.value}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setFilter(category.value)}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all shadow-lg ${
                        filter === category.value
                          ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-green-500/50'
                          : 'bg-white dark:bg-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-500'
                      }`}
                    >
                      <Icon className="text-lg" />
                      <span>{category.label}</span>
                    </motion.button>
                  );
                })}
              </div>

              {/* Results Count */}
              <div className="mt-6 text-center text-gray-600 dark:text-gray-400">
                Showing <span className="font-bold text-green-600 dark:text-green-400">{filteredNews.length}</span> of{' '}
                <span className="font-bold">{news.length}</span> news items
              </div>
            </div>
          </motion.div>

          {/* News Grid */}
          {filteredNews.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <FaNewspaper className="text-6xl text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                No news found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search or filter criteria
              </p>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <AnimatePresence mode="wait">
                {filteredNews.map((item, index) => {
                  const Icon = getIcon(item.category);
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                         to={`/about/news/${item.slug}`} 
                        className="block group h-full"
                      >
                        <div className="bg-white dark:bg-dark-700 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col">
                          {/* Image/Icon Header */}
                          <div className={`relative h-56 bg-gradient-to-br ${getCategoryColor(item.category)} overflow-hidden`}>
                            {item.image ? (
                              <img
                                src={getImageUrl(item.image)}
                                alt={item.image_alt || item.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const parent = e.target.parentElement;
                                  if (parent) {
                                    parent.innerHTML = `
                                      <div class="w-full h-full flex items-center justify-center">
                                        <svg class="text-white text-6xl opacity-50" fill="currentColor" viewBox="0 0 20 20" style="width: 96px; height: 96px;">
                                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                          <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd" />
                                        </svg>
                                      </div>
                                    `;
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Icon className="text-white text-6xl opacity-50" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                            {/* Category Badge */}
                            {item.category && (
                              <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold ${getCategoryBadgeColor(item.category)}`}>
                                {item.category}
                              </span>
                            )}

                            {/* Views Badge */}
                            {item.views !== undefined && (
                              <div className="absolute top-4 right-4 flex items-center gap-1 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full text-white text-xs">
                                <FaEye />
                                <span>{item.views}</span>
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="p-6 flex-1 flex flex-col">
                            <h3 className="font-bold text-xl text-gray-800 dark:text-white mb-3 line-clamp-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition">
                              {item.title}
                            </h3>

                            {item.excerpt && (
                              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3 flex-1">
                                {item.excerpt}
                              </p>
                            )}

                            {/* Meta Info */}
                            <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
                              {item.location && (
                                <div className="flex items-center gap-2">
                                  <FaMapMarkerAlt className="text-green-600 dark:text-green-400" />
                                  <span>{item.location}</span>
                                </div>
                              )}
                              {item.organization && (
                                <div className="flex items-center gap-2">
                                  <FaBuilding className="text-blue-600 dark:text-blue-400" />
                                  <span>{item.organization}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <FaCalendar className="text-purple-600 dark:text-purple-400" />
                                <span>{formatDate(item.published_date || item.created_at)}</span>
                              </div>
                            </div>

                            {/* Read More */}
                            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold group-hover:gap-3 transition-all">
                              <span>Read More</span>
                              <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default News;
